'use client';

import { useTrack } from '@sortlist-frontend/tracking';
import { Fragment, useEffect, useState } from 'react';

import { BriefingDialog } from '_components/Briefing/versions/NormalBriefing/BriefingDialog';
import { WorkDetailModalEmbed } from '_components/common/WorkDetailModal/WorkDetailModalEmbed';
import { ComparatorSidebarEmbed } from '_components/Comparator/Menu/ComparatorSidebarEmbed';
import { NavigationData } from '_components/layout/Layout';
import { PublicAppContextProvider } from '_core/context/public-app-context';
import { useTrackWithoutContext } from '_core/repos/track.repo';
import { Agency, WorkIncludedAttributes } from '_types/public-api';

import { SnackbarProviderLight } from './components/SnackbarProviderLight';
import { DirectoryPageData } from './DirectoryPage';
import { getPageBriefingOptions } from './utils';

type Props = {
  data: DirectoryPageData;
  navigationData: NavigationData;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const Actions = (props: Props) => {
  const { navigationData, data } = props;
  const { origin, resolvedUrl, locale } = navigationData;
  const [active, setActive] = useState(false);

  const { page, works: ssrWorks } = data;

  const worksAgencies = ssrWorks?.included?.filter((work) => work.type === 'agency') as Agency[] | undefined;
  const works = ssrWorks?.included?.filter((work) => work.type === 'work') as WorkIncludedAttributes[] | undefined;

  const { mutateAsync: internalTrack } = useTrackWithoutContext(origin);
  const { track } = useTrack({ app: 'public', locale });

  useEffect(() => {
    internalTrack({
      name: 'longtailLoaded',
      url: location.href,
    });

    track({ slug: 'longtailView', data: { page_id: data.page.data.id, pageId: data.page.data.id } });

    const container = document.getElementById('main-ctn');

    const handleActive = () => {
      if (!active) {
        setActive(true);
        window?.removeEventListener('mousemove', handleActive);
        window?.removeEventListener('click', handleActive);
        window?.removeEventListener('touchstart', handleActive);
        container?.removeEventListener('scroll', handleActive);
      }
    };

    window?.addEventListener('mousemove', handleActive);
    window?.addEventListener('click', handleActive);
    window?.addEventListener('touchstart', handleActive);
    container?.addEventListener('scroll', handleActive);

    return () => {
      window?.removeEventListener('mousemove', handleActive);
      window?.removeEventListener('click', handleActive);
      window?.removeEventListener('touchstart', handleActive);
      container?.removeEventListener('scroll', handleActive);
    };
  }, []);

  return (
    <Fragment>
      <PublicAppContextProvider
        canonical={`${origin}${resolvedUrl}`}
        origin={origin}
        locale={locale}
        briefingOptions={getPageBriefingOptions(page)}>
        <BriefingDialog />
        <ComparatorSidebarEmbed />
        <WorkDetailModalEmbed works={works} agencies={worksAgencies} />
      </PublicAppContextProvider>
      {active ? <SnackbarProviderLight /> : null}
    </Fragment>
  );
};
